// TanStack Query hook for RFC-0018 recently-viewed (the Cmd-K empty-state surface).
//
// Mirrors `useFavorites` (queries/favorites.ts): a read of `GET /api/recently-viewed`
// gated on an authenticated session, selecting the `data` array off the `{ data }`
// envelope. Read-only — recording is the fire-and-forget side effect in the API's
// docs.ts detail branch (RFC-0018 §3), not a client mutation.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { RecentlyViewedItem } from '@portal/shared-types';
import { apiGet } from '@/lib/api-client';
import { useAuth } from '@/context/auth';

/**
 * Hierarchical key factory. `all: ['recently-viewed']` is the family root (mirrors
 * `favoriteKeys`), so a future invalidation can refresh every recents query at once.
 */
export const recentKeys = {
  all: ['recently-viewed'] as const,
  list: () => [...recentKeys.all, 'list'] as const,
};

/**
 * `GET /api/recently-viewed` — the current user's recently-viewed docs, newest-first
 * (server-capped + orphan-pruned). Gated on `isAuthenticated` so it never fires on
 * the login shell. Selects the `data` array off the `{ data }` envelope.
 */
export function useRecentlyViewed(): UseQueryResult<RecentlyViewedItem[], Error> {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: recentKeys.list(),
    queryFn: () => apiGet<{ data: RecentlyViewedItem[] }>('/api/recently-viewed'),
    enabled: isAuthenticated,
    select: (r) => r.data,
  });
}
